import { useMemo } from "react";
import { useMatches, useRouter as useTanstackRouter, useRouterState } from "@tanstack/react-router";
import type { AnyRouter } from "@tanstack/react-router";
import i18next from "i18next";
import { resolveHref } from "./url";
import { getBoundRouter } from "./publicHref";
import type { Query, Url } from "./url";

/** Metadados que src/vite/routes anexa a cada rota (staticData). */
export type PageRouteData = { nextPath: string; splatParam?: string };

type TransitionOptions = { locale?: string; scroll?: boolean; shallow?: boolean };

/** Subconjunto da API do NextRouter usado pelo app. */
export type NextRouter = {
  pathname: string;
  asPath: string;
  query: Query;
  locale: string;
  isReady: boolean;
  push: (url: Url, as?: Url, options?: TransitionOptions) => Promise<boolean>;
  replace: (url: Url, as?: Url, options?: TransitionOptions) => Promise<boolean>;
  back: () => void;
};

export { bindRouter } from "./publicHref";

function requireRouter(): AnyRouter {
  const router = getBoundRouter();
  if (!router) throw new Error("Router ainda não inicializado (bindRouter).");
  return router;
}

async function navigate(router: AnyRouter, url: Url, replace: boolean, options?: TransitionOptions): Promise<boolean> {
  if (options?.locale && options.locale !== i18next.language) await i18next.changeLanguage(options.locale);
  const href = resolveHref(url);
  // O Next ignora navegação para a URL atual; sem isso, efeitos com [router] entram em loop.
  if (href === router.state.location.href) return true;
  await router.navigate({ href, replace, resetScroll: options?.scroll ?? true });
  return true;
}

export function useRouter(): NextRouter {
  const router = useTanstackRouter();
  // Tudo vem da rota RENDERIZADA (state.matches), não de state.location: a location
  // muda no início da navegação, com a página antiga ainda montada. Ler dela fazia
  // efeitos da página antiga (ex.: sync de ?tab no dashboard) navegarem de volta.
  // No Next, URL e página trocam juntas; assim o adapter reproduz isso.
  const match = useMatches({ select: (matches) => matches[matches.length - 1] });
  const fallbackHref = useRouterState({ select: (s) => (s.matches.length ? null : s.location.href) });

  return useMemo<NextRouter>(() => {
    const data = match?.staticData as Partial<PageRouteData> | undefined;
    const { _splat: splat, ...params } = (match?.params ?? {}) as Record<string, string>;
    const search = (match?.search ?? {}) as Query;
    const query: Query = { ...search, ...params };
    if (data?.splatParam) query[data.splatParam] = splat ? splat.split("/") : [];
    const pathname = match?.pathname ?? router.state.location.pathname;
    const href = match ? `${pathname}${router.options.stringifySearch(search)}` : fallbackHref ?? pathname;
    return {
      pathname: data?.nextPath ?? pathname,
      asPath: href,
      query,
      locale: i18next.language,
      isReady: true,
      push: (url, _as, options) => navigate(router, url, false, options),
      replace: (url, _as, options) => navigate(router, url, true, options),
      back: () => router.history.back(),
    };
  }, [router, fallbackHref, match?.id, match?.pathname, match?.search, match?.params, match?.staticData]);
}

/** Equivalente ao `Router` singleton do next/router, para uso fora de componentes. */
const Router = {
  push: (url: Url, _as?: Url, options?: TransitionOptions) => navigate(requireRouter(), url, false, options),
  replace: (url: Url, _as?: Url, options?: TransitionOptions) => navigate(requireRouter(), url, true, options),
  back: () => requireRouter().history.back(),
};

export default Router;
